<template> 

  <div class="">
    <b-container fluid="lg">
      <div class=" mb-2">
        <div class="row mt-2 justify-content-between">
          <h5 class="m-0 p-0">CVSS - 3.1 </h5> 

          <div class="row" v-if="severity.name">
            <p class='row mb-1 mx-2'> <Impact :impact="severity?.name || 'impact-none'" class="mt-1 mr-1" /></p>
            <strong class="mr-1">{{$ml.get(severity?.name.replace('impact-',''))}}</strong> 
            <strong v-if="score">- {{score}}</strong> 
          </div>

        </div>
        <h6 class="">Common Vulnerability Scoring System</h6>
      </div>
    </b-container>
  
    <div class='d-flex flex-column mt-2 itensCvss row col-24'>  

      <div class="accordion" role="tablist">
        <b-collapse  id="attackVector" accordion="my-accordion" role="tabpanel">
          <b-card> 
            <div class="mb-3"> 
              <button type="button" aria-label="Close" class="close pb-0 mb-0 pointer" v-b-toggle.attackVector>×</button>
            </div> 
            <h6 class="mb-3">Attack Vector</h6>
            <p><b>Network:</b> (<i>worst</i>) The vulnerable component is bound to the network stack and the set of possible attackers extends beyond the other options listed below, up to and including the entire Internet. Such a vulnerability is often termed “remotely exploitable” and can be thought of as an attack being exploitable at the protocol level one or more network hops away (e.g., across one or more routers).</p> 
            <p><b>Adjacent:</b> (<i>worse</i>) The vulnerable component is bound to the network stack, but the attack is limited at the protocol level to a logically adjacent topology. This can mean an attack must be launched from the same shared physical (e.g., Bluetooth or IEEE 802.11) or logical (e.g., local IP subnet) network, or from within a secure or otherwise limited administrative domain (e.g., MPLS, secure VPN to an administrative network zone). One example of an Adjacent attack would be an ARP (IPv4) or neighbor discovery (IPv6) flood leading to a denial of service on the local LAN segment.</p>
            <p><b>Local:</b> (<i>bad</i>) The vulnerable component is not bound to the network stack and the attacker's path is via read/write/execute capabilities. Either: <ul><li>the attacker exploits the vulnerability by accessing the target system locally (e.g., keyboard, console), or remotely (e.g., SSH);</li><li>or the attacker relies on User Interaction by another person to perform actions required to exploit the vulnerability (e.g., using social engineering techniques to trick a legitimate user into opening a malicious document).</li></ul></p>
            <p><b>Physical:</b> (<i>bad</i>) The attack requires the attacker to physically touch or manipulate the vulnerable component. Physical interaction may be brief (e.g., evil maid attack) or persistent. An example of such an attack is a cold boot attack in which an attacker gains access to disk encryption keys after physically accessing the target system. Other examples include peripheral attacks via FireWire/USB Direct Memory Access (DMA).</p>
          </b-card>
        </b-collapse>

        <b-collapse  id="attackComplexity" accordion="my-accordion" role="tabpanel">
          <b-card> 
            <div class="mb-3"> 
              <button type="button" aria-label="Close" class="close pb-0 mb-0 pointer" v-b-toggle.attackComplexity>×</button>
            </div> 
            <h6 class="mb-3">Attack Complexity</h6>
            <p><b>Low:</b> (<i>worst</i>) Specialized access conditions or extenuating circumstances do not exist. An attacker can expect repeatable success when attacking the vulnerable component.</p> 
            <p><b>High:</b> (<i>bad</i>) A successful attack depends on conditions beyond the attackers control. That is, a successful attack cannot be accomplished at will, but requires the attacker to invest in some measurable amount of effort in preparation or execution against the vulnerable component before a successful attack can be expected.</p>
          </b-card>
        </b-collapse>

        <b-collapse  id="privilegesRequired" accordion="my-accordion" role="tabpanel">
          <b-card> 
            <div class="mb-3"> 
              <button type="button" aria-label="Close" class="close pb-0 mb-0 pointer" v-b-toggle.privilegesRequired>×</button>
            </div> 
            <h6 class="mb-3">Privileges Required</h6>
            <p><b>None:</b> (<i>worst</i>) The attacker is unauthorized prior to attack, and therefore does not require any access to settings or files of the the vulnerable system to carry out an attack.</p> 
            <p><b>Low:</b> (<i>Worse</i>) The attacker requires privileges that provide basic user capabilities that could normally affect only settings and files owned by a user. Alternatively, an attacker with Low privileges has the ability to access only non-sensitive resources.</p> 
            <p><b>High:</b> (<i>bad</i>) The attacker requires privileges that provide significant (e.g., administrative) control over the vulnerable component allowing access to component-wide settings and files.</p>
          </b-card>
        </b-collapse>

        <b-collapse  id="userInteraction" accordion="my-accordion" role="tabpanel">
          <b-card> 
            <div class="mb-3"> 
              <button type="button" aria-label="Close" class="close pb-0 mb-0 pointer" v-b-toggle.userInteraction>×</button>
            </div> 
            <h6 class="mb-3">User Interaction</h6>
            <p><b>None:</b> (<i>worst</i>) The vulnerable system can be exploited without interaction from any user.</p> 
            <p><b>Required:</b> (<i>bad</i>) Successful exploitation of this vulnerability requires a user to take some action before the vulnerability can be exploited. For example, a successful exploit may only be possible during the installation of an application by a system administrator.</p> 
          </b-card>
        </b-collapse>

        <b-collapse  id="scope" accordion="my-accordion" role="tabpanel"> 
          <b-card> 
            <div class="mb-3"> 
              <button type="button" aria-label="Close" class="close pb-0 mb-0 pointer" v-b-toggle.scope>×</button>
            </div> 
            <h6 class="mb-3">Scope</h6>
            <p><b>Changed:</b> (<i>worst</i>) An exploited vulnerability can affect resources beyond the security scope managed by the security authority of the vulnerable component. In this case, the vulnerable component and the impacted component are different and managed by different security authorities.</p> 
            <p><b>Unchanged:</b> (<i>bad</i>) An exploited vulnerability can only affect resources managed by the same security authority. In this case, the vulnerable component and the impacted component are either the same, or both are managed by the same security authority.</p> 
          </b-card>
        </b-collapse>

        <b-collapse  id="confidentiality" accordion="my-accordion" role="tabpanel">
          <b-card> 
            <div class="mb-3"> 
              <button type="button" aria-label="Close" class="close pb-0 mb-0 pointer" v-b-toggle.confidentiality>×</button>
            </div> 
            <h6 class="mb-3">Confidentiality</h6>
            <p><b>None:</b> (<i>good</i>) There is no loss of confidentiality within the impacted component.</p> 
            <p><b>Low:</b> (<i>bad</i>) There is some loss of confidentiality. Access to some restricted information is obtained, but the attacker does not have control over what information is obtained, or the amount or kind of loss is limited. The information disclosure does not cause a direct, serious loss to the impacted component.</p> 
            <p><b>High:</b> (<i>worst</i>) There is a total loss of confidentiality, resulting in all resources within the impacted component being divulged to the attacker. Alternatively, access to only some restricted information is obtained, but the disclosed information presents a direct, serious impact. For example, an attacker steals the administrators password, or private encryption keys of a web server.</p> 
          </b-card>
        </b-collapse>

        <b-collapse  id="integrity" accordion="my-accordion" role="tabpanel">
          <b-card> 
            <div class="mb-3"> 
              <button type="button" aria-label="Close" class="close pb-0 mb-0 pointer" v-b-toggle.integrity>×</button>
            </div>
            <h6 class="mb-3">Integrity</h6>
            <p><b>None:</b> (<i>good</i>) There is no loss of integrity within the impacted component.</p> 
            <p><b>Low:</b> (<i>bad</i>) Modification of data is possible, but the attacker does not have control over the consequence of a modification, or the amount of modification is limited. The data modification does not have a direct, serious impact on the impacted component.</p> 
            <p><b>High:</b> (<i>worst</i>) There is a total loss of integrity, or a complete loss of protection. For example, the attacker is able to modify any/all files protected by the impacted component. Alternatively, only some files can be modified, but malicious modification would present a direct, serious consequence to the impacted component.</p> 
          </b-card>
        </b-collapse>

        <b-collapse  id="availability" accordion="my-accordion" role="tabpanel"> 
          <b-card> 
            <div class="mb-3"> 
              <button type="button" aria-label="Close" class="close pb-0 mb-0 pointer" v-b-toggle.availability>×</button>
            </div>
            <h6 class="mb-3">Availability</h6>
            <p><b>None:</b> (<i>good</i>) There is no impact to availability within the impacted component.</p> 
            <p><b>Low:</b> (<i>bad</i>) Performance is reduced or there are interruptions in resource availability. Even if repeated exploitation of the vulnerability is possible, the attacker does not have the ability to completely deny service to legitimate users. The resources in the impacted component are either partially available all of the time, or fully available only some of the time, but overall there is no direct, serious consequence to the impacted component.</p> 
            <p><b>High:</b> (<i>worst</i>) There is a total loss of availability, resulting in the attacker being able to fully deny access to resources in the impacted component; this loss is either sustained (while the attacker continues to deliver the attack) or persistent (the condition persists even after the attack has completed). Alternatively, the attacker has the ability to deny some availability, but the loss of availability presents a direct, serious consequence to the impacted component (e.g., the attacker cannot disrupt existing connections, but can prevent new connections; the attacker can repeatedly exploit a vulnerability that, in each instance of a successful attack, leaks a only small amount of memory, but after repeated exploitation causes a service to become completely unavailable).</p> 
          </b-card>
        </b-collapse>

      </div>
  
      <div class="row mt-4"> 
        <div class="col-12">
          <div>
            <label>Attack Vector</label>
            <img v-b-toggle.attackVector src="../../src/assets/icons/interrogation.png" class="ico-15 mx-1 mb-1 pointer" />
            
            <b-form-group >  
              <b-form-radio-group buttons >  
                <b-form-radio v-model="selectedAV" value="N">Network</b-form-radio> 
                <b-form-radio v-model="selectedAV" value="A">Adjacent</b-form-radio>  
                <b-form-radio v-model="selectedAV" value="L">Local</b-form-radio>   
                <b-form-radio v-model="selectedAV" value="P">Physical</b-form-radio>  
              </b-form-radio-group> 
      
            </b-form-group> 
          </div>
  
          <div>
            <label>Attack Complexity</label>
            <img v-b-toggle.attackComplexity  src="../../src/assets/icons/interrogation.png" class="ico-15 mx-1 mb-1 pointer" />
            <b-form-group  >
              <b-form-radio-group buttons > 
                <b-form-radio v-model="selectedAC" value="L">Low</b-form-radio> 
                <b-form-radio v-model="selectedAC" value="H">High</b-form-radio> 
              </b-form-radio-group>
            </b-form-group> 
          </div>
    
          <div>
            <label>Privileges Required</label>
            <img v-b-toggle.privilegesRequired src="../../src/assets/icons/interrogation.png" class="ico-15 mx-1 mb-1 pointer" />
            
            <b-form-group  >
              <b-form-radio-group buttons >  
                <b-form-radio v-model="selectedPR" value="N">None</b-form-radio> 
                <b-form-radio v-model="selectedPR" value="L">Low</b-form-radio>  
                <b-form-radio v-model="selectedPR" value="H">High</b-form-radio>  
              </b-form-radio-group>
            </b-form-group> 
          </div> 
      
          <div>
            <label>User Interaction</label>
            <img v-b-toggle.userInteraction src="../../src/assets/icons/interrogation.png" class="ico-15 mx-1 mb-1 pointer" />
            
            <b-form-group >
              <b-form-radio-group buttons >  
                <b-form-radio v-model="selectedUI" value="N">None</b-form-radio>  
                <b-form-radio v-model="selectedUI" value="R">Required</b-form-radio>  
              </b-form-radio-group>
            </b-form-group> 
          </div>
        </div> 
        <div class="col-12"> 
          <div>
            <label>Scope</label>
            <img v-b-toggle.scope src="../../src/assets/icons/interrogation.png" class="ico-15 mx-1 mb-1 pointer" />
            
            <b-form-group >
              <b-form-radio-group buttons >  
                <b-form-radio v-model="selectedS" value="C">Changed</b-form-radio>  
                <b-form-radio v-model="selectedS" value="U">Unchanged</b-form-radio>  
              </b-form-radio-group>
            </b-form-group> 
          </div>
      
          <div>
            <label>Confidentiality</label>
            <img v-b-toggle.confidentiality src="../../src/assets/icons/interrogation.png" class="ico-15 mx-1 mb-1 pointer" />
            
            <b-form-group >
              <b-form-radio-group buttons >  
                <b-form-radio v-model="selectedC" value="N">None</b-form-radio>  
                <b-form-radio v-model="selectedC" value="L">Low</b-form-radio>  
                <b-form-radio  v-model="selectedC" value="H">High</b-form-radio>  
              </b-form-radio-group>
            </b-form-group> 
          </div>
          
          <div>
            <label>Integrity</label>
            <img v-b-toggle.integrity src="../../src/assets/icons/interrogation.png" class="ico-15 mx-1 mb-1 pointer" />
            
            <b-form-group >
              <b-form-radio-group buttons >   
                <b-form-radio v-model="selectedI" value="N">None</b-form-radio> 
                <b-form-radio v-model="selectedI" value="L">Low</b-form-radio>  
                <b-form-radio v-model="selectedI" value="H">High</b-form-radio>  
              </b-form-radio-group>
            </b-form-group> 
          </div>
      
          <div>
            <label>Availability</label>
            <img v-b-toggle.availability src="../../src/assets/icons/interrogation.png" class="ico-15 mx-1 mb-1 pointer" />
            
            <b-form-group >
              <b-form-radio-group buttons >  
                <b-form-radio v-model="selectedA" value="N">None</b-form-radio>  
                <b-form-radio v-model="selectedA" value="L">Low</b-form-radio>  
                <b-form-radio v-model="selectedA" value="H">High</b-form-radio>   
              </b-form-radio-group>
            </b-form-group> 
          </div>
        </div>

      </div>
  
    </div>
    
    <div class='d-flex flex-column mt-2'>
      <p class='mb-2'><b>Vector:</b> AV:{{selectedAV}}/AC:{{selectedAC}}/PR:{{selectedPR}}/UI:{{selectedUI}}/S:{{selectedS}}/C:{{selectedC}}/I:{{selectedI}}/A:{{selectedA}}</p> 
      
    </div> 
  </div> 
   
</template>
 
<script> 
  
export default {
  name: "cvss", 
  props:['resetCvss'],
  data() {
    return { 
      bg: {
          AV: 'Attack Vector',
          AC: 'Attack Complexity',
          PR: 'Privileges Required',
          UI: 'User Interaction',
          S: 'Scope',
          C: 'Confidentiality',
          I: 'Integrity',
          A: 'Availability'
      },
      
      bme: {},
      bmoReg: {
        AV: 'NALP',
        AC: 'LH',
        C: 'C',
        I: 'C',
        A: 'C'
      },
      bmgReg: {
        AV: 'NALP',
        AC: 'LH',
        PR: 'NLH',
        UI: 'NR',
        S: 'CU',
        C: 'HLN',
        I: 'HLN',
        A: 'HLN'
      },
      severityRatings: [{
          name: "impact-none",
          bottom: 0.0,
          top: 0.0
      }, {
          name: "impact-low",
          bottom: 0.1,
          top: 3.9
      }, {
          name: "impact-medium",
          bottom: 4.0,
          top: 6.9
      }, {
          name: "impact-high",
          bottom: 7.0,
          top: 8.9
      }, {
          name: "impact-critical",
          bottom: 9.0,
          top: 10.0
      }],

      score: 0,
      severity: [],
      vector: '',
 
      selectedAV: '', 
      selectedAC: '', 
      selectedPR: '', 
      selectedUI: '', 
      selectedS: '', 
      selectedC: '', 
      selectedI: '', 
      selectedA: '', 
    };
  },
  computed: {
    btnStates() {
      return this.buttons.map(btn => btn.state)
    }
  },
  watch: {
   
    selectedAV: function () {
      this.getMetric();
    }, 
    selectedAC: function () {
      this.getMetric();
    }, 
    selectedPR: function () {
      this.getMetric();
    }, 
    selectedUI: function () {
      this.getMetric();
    }, 
    selectedS: function () {
      this.getMetric();
    }, 
    selectedC: function () {
      this.getMetric();
    }, 
    selectedI: function () {
      this.getMetric();
    }, 
    selectedA: function () {
      this.getMetric();
    }, 

    resetCvss: function () {
      this.resetModal();
    }, 
  },
 
  mounted() { 
    
  },
  methods: { 

    resetModal(){
  
      this.score = 0;
      this.severity = [];
      this.vector = '';
  
      this.selectedAV = ''; 
      this.selectedAC = ''; 
      this.selectedPR = ''; 
      this.selectedUI = ''; 
      this.selectedS = '';
      this.selectedC = '';
      this.selectedI = '';
      this.selectedA = '';
    },
 
    severityRating(score){
      var i;
      var severityRatingLength = this.severityRatings.length;
      for (i = 0; i < severityRatingLength; i++) {
          if (score >= this.severityRatings[i].bottom && score <= this.severityRatings[i].top) {
              return this.severityRatings[i];
          }
      }
      return {
          name: "?",
          bottom: 'Not',
          top: 'defined'
      };
    },


    calculate(teste) {
      var exploitabilityCoefficient = 8.22;
      var scopeCoefficient = 1.08;

      // Define associative arrays mapping each metric value to the constant used in the CVSS scoring formula.
      var Weight = {
          AV: {
              N: 0.85,
              A: 0.62,
              L: 0.55,
              P: 0.2
          },
          AC: {
              H: 0.44,
              L: 0.77
          },
          PR: {
              U: {
                  N: 0.85,
                  L: 0.62,
                  H: 0.27
              },
              // These values are used if Scope is Unchanged
              C: {
                  N: 0.85,
                  L: 0.68,
                  H: 0.5
              }
          },
          // These values are used if Scope is Changed
          UI: {
              N: 0.85,
              R: 0.62
          },
          S: {
              U: 6.42,
              C: 7.52
          },
          C: {
              N: 0,
              L: 0.22,
              H: 0.56
          },
          I: {
              N: 0,
              L: 0.22,
              H: 0.56
          },
          A: {
              N: 0,
              L: 0.22,
              H: 0.56
          }
          // C, I and A have the same weights
      };

      var p;
      var val = {}, metricWeight = {};
        
      try {
          for (p in this.bg) {
              val[p] = teste[p];
              metricWeight[p] = Weight[p][val[p]];
          }
      } catch (err) {
          return err; // TODO: need to catch and return sensible error value & do a better job of specifying *which* parm is at fault.
      }
      metricWeight.PR = Weight.PR[val.S][val.PR];

   
      // CALCULATE THE CVSS BASE SCORE 3.0
      // var roundUp1 = function Roundup(input) {
      //     var int_input = Math.round(input * 100000);
      //     if (int_input % 10000 === 0) {
      //         return int_input / 100000
      //     } else {
      //         return (Math.floor(int_input / 10000) + 1) / 10
      //     }
      // };

      // CALCULATE THE CVSS BASE SCORE 3.1
      const roundUp1 = function(input) {
        return Math.ceil(input * 10) / 10;
      };

      try {
      var baseScore, impactSubScore;
      var impactSubScoreMultiplier = (1 - ((1 - metricWeight.C) * (1 - metricWeight.I) * (1 - metricWeight.A)));
      if (val.S === 'U') {
          impactSubScore = metricWeight.S * impactSubScoreMultiplier;
      } else {
          impactSubScore = metricWeight.S * (impactSubScoreMultiplier - 0.029) - 3.25 * Math.pow(impactSubScoreMultiplier - 0.02, 15);
      }
      var exploitabalitySubScore = exploitabilityCoefficient * metricWeight.AV * metricWeight.AC * metricWeight.PR * metricWeight.UI;
      if (impactSubScore <= 0) {
          baseScore = 0;
      } else {
          if (val.S === 'U') {
              baseScore = roundUp1(Math.min((exploitabalitySubScore + impactSubScore), 10));
          } else {
              baseScore = roundUp1(Math.min((exploitabalitySubScore + impactSubScore) * scopeCoefficient, 10));
          }
      }

      return baseScore.toFixed(1);
      } catch (err) {
          return err;
      }
    },


    getMetric() {

      if(this.selectedAV && this.selectedAC && this.selectedPR && this.selectedUI && this.selectedS && this.selectedC && this.selectedI && this.selectedA )
      {
        var s = this.calculate({
            'AV': this.selectedAV,
            'AC': this.selectedAC,
            'PR': this.selectedPR, 
            'UI': this.selectedUI, 
            'S': this.selectedS, 
            'C': this.selectedC, 
            'I': this.selectedI, 
            'A': this.selectedA}); 
  
        this.score = s

        this.severity.vector = `AV:${this.selectedAV}/AC:${this.selectedAC}/PR:${this.selectedPR}/UI:${this.selectedUI}/S:${this.selectedS}/C:${this.selectedC}/I:${this.selectedI}/A:${this.selectedA}`
       
        var rating = this.severityRating(s);
        this.severity.name = rating.name;
        this.severity.score = this.score;
        this.$emit('alterSeverity', this.severity);
      }

    }, 
  },
};
</script>
<style lang="scss" >
  // .btn_cvss {
    
  // }
  .itensCvss{
    .col-form-label {
      padding-top: calc(0.375rem + 1px);
      padding-bottom: calc(0.275rem + 1px);
      margin-bottom: 0;
      font-size: 0.94rem;
      line-height: 1.3;
      font-weight: 400;
    }
    .btn {
      padding: 7px 12px 6px;
      font-size: 0.90rem;
      color: #730BA9 !important;
      background-color: #fdfdfd !important;
      border: 1px solid #730BA9;
      transition: 0.5s; 
      margin-bottom: 0px !important;
    } 
    .active {
      padding: 7px 12px 6px;
      font-size: 0.90rem;
      color: #ffffff !important;
      background-color: #730BA9 !important;
      border: 1px solid #730BA9; 
      margin-bottom: 0px !important;
    } 
  }
</style>
